import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, Plus, FileText, Home, Users, ClipboardCheck, BookOpen, PlusCircle, ChartBar, Award as AwardIcon, DollarSign, Inbox as InboxIcon } from '../../components/CustomIcons';
import { Btn, Chip, Mono, ScreenHeader } from '../Prototype';
import { StatCard, SectionTitle, StaffShell, Avatar, Badge, useUI } from './shared';
import { postNotification, useNotifications, markAllRead, markRead } from './notifications';

/* Shared identity map */
export const IDENTITIES = {
  facility_admin: { name: 'Lt. Carlos Mendoza', role: 'Education Director', facility: 'Collin County DC' },
  training_provider: { name: 'Dr. Sarah Martinez', role: 'Director, Correctional Programs', facility: 'Collin College' },
  case_manager: { name: 'Maya Reyes', role: 'Case Manager', facility: 'Collin County DC' },
  instructor: { name: 'Dr. Sandra Kim', role: 'Instructor', facility: 'Collin County DC' },
  employer: { name: 'James Whitfield', role: 'Talent Acquisition', facility: 'TechForce Solutions' },
  admin: { name: 'LaVar McCline', role: 'TTI Admin', facility: 'Tech Tradesmith Institute' },
  youth_learner: { name: 'Jordan M.', role: 'Youth Learning Program', facility: 'Collin County Youth Center' },
  professional_learner: { name: 'Alex T.', role: 'Professional Learner', facility: 'North Texas Community Education Center' },
};

/* ==========================================================
   Shared Sign-In Screen (auto-advances in 1.5s)
   ========================================================== */

export const SignInScreen = ({ role, onContinue }) => {
  const ident = IDENTITIES[role] || IDENTITIES.admin;
  useEffect(() => {
    const t = setTimeout(onContinue, 1500);
    return () => clearTimeout(t);
  }, [onContinue]);
  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center px-6 text-center"
      style={{ background: '#0f2744', color: '#fafaf9', animation: 'proto-fade-up 300ms ease-out both' }}
      data-testid={`proto-signin-${role}`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#f97316', fontFamily: 'var(--p-font-mono)' }}>AssistivOS · TTI</div>
      <Avatar name={ident.name} size={72} tone="orange" />
      <h1 className="p-display text-[24px] mt-4" style={{ color: '#fafaf9', margin: 0 }}>{ident.name}</h1>
      <div className="text-[14px] mt-1" style={{ color: 'rgba(250,250,249,0.72)' }}>
        {ident.role} · {ident.facility}
      </div>
      <button
        onClick={onContinue}
        data-testid="proto-signin-continue"
        className="mt-8 rounded-[12px] font-semibold text-[15px] min-h-[48px] px-8"
        style={{ background: '#f97316', color: '#0f2744' }}
      >
        Continue to Dashboard
      </button>
    </div>
  );
};

/* ==========================================================
   Cross-role state: approval queue shared FA ↔ TP
   ========================================================== */

const DEFAULT_APPROVALS = [
  {
    id: 'hvac-l4-update',
    title: 'HVAC Module Update — Lesson 4 Revised',
    provider: 'Collin College',
    submitted: '2 days ago',
    kind: 'update',
    summary: 'Updated refrigerant handling procedures to reflect 2024 EPA 608 standards.',
    status: 'pending',
  },
  {
    id: 'solar-new',
    title: 'New Module: Solar Installation Fundamentals',
    provider: 'Collin College',
    submitted: '4 days ago',
    kind: 'new',
    credential: 'NABCEP PV Associate',
    hours: 40,
    status: 'pending',
  },
];

const readApprovals = () => {
  try {
    const raw = localStorage.getItem('assistivos_approvals');
    if (raw) return JSON.parse(raw);
  } catch (_e) { /* ignore */ }
  return DEFAULT_APPROVALS;
};
const writeApprovals = (arr) => {
  try { localStorage.setItem('assistivos_approvals', JSON.stringify(arr)); } catch (_e) { /* ignore */ }
};

/* ==========================================================
   FACILITY ADMIN
   ========================================================== */

const FA_NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'inbox', label: 'Inbox', icon: InboxIcon },
  { key: 'approvals', label: 'Approvals', icon: ClipboardCheck },
  { key: 'staff', label: 'Staff', icon: Users },
  { key: 'reports', label: 'Reports', icon: FileText },
];

const useFAState = () => {
  const [tab, setTab] = useState('dashboard');
  const [approvals, setApprovals] = useState(readApprovals);
  const update = (next) => { setApprovals(next); writeApprovals(next); };
  return { tab, setTab, approvals, update };
};

const FA_Dashboard = ({ approvals, setTab }) => {
  const [, unreadFA] = useNotifications('facility_admin');
  const pending = approvals.filter((a) => a.status === 'pending').length;
  return (
    <>
      <ScreenHeader title="Facility Dashboard" subtitle={`${IDENTITIES.facility_admin.name} · ${IDENTITIES.facility_admin.facility}`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section className="grid grid-cols-2 gap-2" aria-label="Facility stats">
          <StatCard label="Active Learners" value={94} />
          <StatCard label="Staff Members" value={12} />
          <StatCard label="WCAG Compliance" value="89%" />
          <StatCard label="Pending Approvals" value={pending} accent testId="fa-pending-count" />
        </section>
        {unreadFA > 0 && (
          <section aria-label="Inbox prompt">
            <button
              onClick={() => setTab('inbox')}
              data-testid="fa-open-inbox"
              className="w-full rounded-[12px] p-4 flex items-center justify-between text-left"
              style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid #f97316' }}
            >
              <div>
                <div className="p-display text-[16px]" style={{ color: '#0f2744' }}>New messages in Inbox</div>
                <div className="text-[13px] text-[#64748b] mt-0.5">
                  <Mono>{unreadFA}</Mono> unread ping{unreadFA === 1 ? '' : 's'} from training providers &amp; staff
                </div>
              </div>
              <span className="text-[13px] font-semibold" style={{ color: '#c2410c' }}>Open →</span>
            </button>
          </section>
        )}
        <section aria-label="Approvals prompt">
          <button
            onClick={() => setTab('approvals')}
            data-testid="fa-open-approvals"
            className="w-full rounded-[12px] p-4 flex items-center justify-between text-left"
            style={{ background: pending > 0 ? 'rgba(249,115,22,0.10)' : '#f1f5f9', border: `1px solid ${pending > 0 ? '#f97316' : 'transparent'}` }}
          >
            <div>
              <div className="p-display text-[16px]" style={{ color: '#0f2744' }}>Content Approval Queue</div>
              <div className="text-[13px] text-[#64748b] mt-0.5">
                <Mono>{pending}</Mono> item{pending === 1 ? '' : 's'} awaiting your review from training providers
              </div>
            </div>
            <span className="text-[13px] font-semibold" style={{ color: '#c2410c' }}>Review →</span>
          </button>
        </section>
        <section aria-label="Activity">
          <SectionTitle>Recent Activity</SectionTitle>
          <ul className="list-none p-0 space-y-2">
            {[
              'Dr. Sandra Kim added 3 learner flags',
              'HVAC Fundamentals — 4 completions this week',
              'Collin College submitted 2 new courses for review',
              'Jonathan T. earned CompTIA A+ credential',
              'Maya Reyes updated 6 reentry plans',
            ].map((t, i) => (
              <li key={i} className="rounded-[12px] bg-[#f1f5f9] p-3 text-[14px] text-[#0f2744]">{t}</li>
            ))}
          </ul>
        </section>
      </main>
    </>
  );
};

const FA_Approvals = ({ approvals, update, setTab }) => {
  const { showModal, showToast } = useUI();
  const [note, setNote] = useState('');
  const decide = (a, decision) => {
    const next = approvals.map((x) => x.id === a.id ? { ...x, status: decision } : x);
    update(next);
    showToast(decision === 'approved' ? `${a.title} approved and published.` : 'Revision request sent to Collin College.', 'success');
    // Cross-role notification → Training Provider inbox
    postNotification({
      from: 'facility_admin',
      to: 'training_provider',
      type: decision === 'approved' ? 'course_approved' : 'revision_requested',
      title: decision === 'approved' ? `Approved: ${a.title}` : `Revision requested: ${a.title}`,
      body: decision === 'approved'
        ? 'Course is now live and available to learners.'
        : 'The Facility Admin left feedback for revision. Please review.',
      meta: { approvalId: a.id },
    });
  };
  const requestRevision = (a) => showModal({
    title: `Request revision on ${a.title}?`,
    body: 'Add a note for the training provider. They will re-submit with your feedback.',
    input: { placeholder: 'What needs to change?', value: note, onChange: setNote },
    confirmLabel: 'Send Request',
    onConfirm: () => { decide(a, 'revision'); setNote(''); },
  });
  const pending = approvals.filter((a) => a.status === 'pending');
  return (
    <>
      <ScreenHeader title="Content Approval Queue" subtitle={`${pending.length} pending review`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        {pending.length === 0 && (
          <div className="text-center text-[14px] text-[#64748b] py-8">No pending items. All caught up.</div>
        )}
        {pending.map((a) => (
          <article key={a.id} className="rounded-[12px] bg-[#f1f5f9] p-4">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="p-display text-[17px]" style={{ color: '#0f2744', margin: 0 }}>{a.title}</h2>
                  <Badge tone={a.kind === 'new' ? 'orange' : 'navy'}>{a.kind === 'new' ? 'New Module' : 'Update'}</Badge>
                </div>
                <div className="text-[12px] text-[#64748b] mt-1">Submitted by {a.provider} · {a.submitted}</div>
              </div>
            </div>
            {a.summary && <p className="text-[14px] text-[#1c1c1e] mt-3 leading-relaxed">{a.summary}</p>}
            {a.kind === 'new' && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Chip>{a.credential}</Chip>
                <Chip><Mono>{a.hours}</Mono> clock hours</Chip>
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Btn variant="ghost" onClick={() => requestRevision(a)} data-testid={`fa-revise-${a.id}`}>Request Revision</Btn>
              <Btn onClick={() => showModal({
                title: `Approve ${a.title}?`,
                body: 'Approving publishes this content to your facility. Instructors and learners will see it immediately.',
                confirmLabel: 'Approve & Publish',
                onConfirm: () => decide(a, 'approved'),
              })} data-testid={`fa-approve-${a.id}`}>Approve</Btn>
            </div>
          </article>
        ))}
      </main>
    </>
  );
};

const FA_NOTIF_META = {
  course_resubmitted: { label: 'Resubmission', icon: '📝', accent: '#f97316', bg: 'rgba(249,115,22,0.10)', border: 'rgba(249,115,22,0.35)' },
  _default:           { label: 'Notification', icon: '📬', accent: '#0f2744', bg: '#f1f5f9',                 border: '#e2e8f0' },
};

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const dSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (dSec < 60) return `${dSec}s ago`;
  if (dSec < 3600) return `${Math.round(dSec / 60)}m ago`;
  if (dSec < 86400) return `${Math.round(dSec / 3600)}h ago`;
  return `${Math.round(dSec / 86400)}d ago`;
};

const FA_Inbox = ({ setTab }) => {
  const [items, unread] = useNotifications('facility_admin');
  const onOpen = (n) => {
    markRead(n.id);
    if (n.type === 'course_resubmitted') {
      setTab('approvals');
    }
  };
  return (
    <>
      <ScreenHeader
        title="Inbox"
        subtitle={`${unread} unread · ${items.length} total`}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3" data-testid="fa-inbox">
        {unread > 0 && (
          <button
            type="button"
            onClick={() => markAllRead('facility_admin')}
            data-testid="fa-inbox-mark-all-read"
            className="text-[12px] font-semibold text-[#c2410c] underline underline-offset-2 hover:opacity-75"
          >
            Mark all as read
          </button>
        )}
        {items.length === 0 && (
          <div className="rounded-[12px] bg-[#f1f5f9] p-6 text-center">
            <div className="p-display text-[16px] text-[#0f2744]">You&apos;re all caught up</div>
            <p className="text-[13px] text-[#64748b] mt-2">Cross-role pings from training providers, case managers, and staff will land here.</p>
          </div>
        )}
        <ul className="list-none p-0 space-y-2">
          {items.map((n) => {
            const meta = FA_NOTIF_META[n.type] || FA_NOTIF_META._default;
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => onOpen(n)}
                  data-testid={`fa-inbox-item-${n.type}`}
                  className="w-full text-left rounded-[12px] p-4 transition-colors hover:brightness-[0.98]"
                  style={{
                    background: n.read ? '#fafaf9' : meta.bg,
                    border: `1px solid ${n.read ? '#e2e8f0' : meta.border}`,
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-[22px] shrink-0" aria-hidden="true">{meta.icon}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge tone="orange">{meta.label}</Badge>
                        {!n.read && (
                          <span data-testid={`fa-inbox-unread-${n.id}`} className="inline-block w-2 h-2 rounded-full" style={{ background: meta.accent }} aria-label="Unread" />
                        )}
                        <span className="text-[11px] text-[#94a3b8] p-mono ml-auto">{formatRelativeTime(n.at)}</span>
                      </div>
                      <div className="text-[14px] font-semibold text-[#0f2744] mt-1">{n.title}</div>
                      {n.body && <div className="text-[12px] text-[#475569] mt-1 leading-relaxed">{n.body}</div>}
                      {n.type === 'course_resubmitted' && (
                        <div className="text-[12px] font-semibold mt-2" style={{ color: meta.accent }}>Go to Approvals →</div>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </main>
    </>
  );
};

const FA_Stub = ({ title, subtitle, activeKey, setTab }) => (
  <>
    <ScreenHeader title={title} subtitle={subtitle} />
    <main className="overflow-y-auto flex-1 px-6 py-5">
      <div className="rounded-[12px] bg-[#f1f5f9] p-6 text-center">
        <div className="p-display text-[16px] text-[#0f2744]">Coming in the next pass</div>
        <p className="text-[13px] text-[#64748b] mt-2">This view lands in Pass 2 alongside the persistent sidebar shell.</p>
      </div>
    </main>
  </>
);

export const FacilityAdmin = () => {
  const s = useFAState();
  const [, unreadFA] = useNotifications('facility_admin');
  const pendingApprovals = s.approvals.filter((a) => a.status === 'pending').length;
  let screen;
  if (s.tab === 'inbox') screen = <FA_Inbox setTab={s.setTab} />;
  else if (s.tab === 'approvals') screen = <FA_Approvals {...s} />;
  else if (s.tab === 'staff') screen = <FA_Stub title="Staff Directory" subtitle="Collin County DC" activeKey="staff" setTab={s.setTab} />;
  else if (s.tab === 'reports') screen = <FA_Stub title="Reports" subtitle="Facility outcomes" activeKey="reports" setTab={s.setTab} />;
  else screen = <FA_Dashboard {...s} />;
  return (
    <StaffShell
      items={FA_NAV}
      activeKey={s.tab}
      onChange={s.setTab}
      badge={{ initial: 'M', name: IDENTITIES.facility_admin.name.replace('Lt. ', ''), role: 'Facility Admin' }}
      itemBadges={{ approvals: pendingApprovals, inbox: unreadFA }}
      footerUnread={unreadFA}
    >
      {screen}
    </StaffShell>
  );
};

/* ==========================================================
   TRAINING PROVIDER
   ========================================================== */

const TP_NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'courses', label: 'Courses', icon: BookOpen },
  { key: 'submit', label: 'Submit', icon: PlusCircle },
  { key: 'analytics', label: 'Analytics', icon: ChartBar },
];

const TP_COURSES = [
  { name: 'HVAC Fundamentals', enrolled: 94, completions: 68, status: 'active', credential: 'EPA 608', hours: 50, completionPct: 72 },
  { name: 'AI Foundations', enrolled: 87, completions: 61, status: 'active', credential: 'CompTIA AI+', hours: 40, completionPct: 70 },
  { name: 'Medical Assistant Essentials', enrolled: 66, completions: 48, status: 'active', credential: 'NHA CCMA', hours: 45, completionPct: 73 },
];

const useTPState = () => {
  const [tab, setTab] = useState('dashboard');
  const [approvals, setApprovals] = useState(readApprovals);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [resubmitFromApprovalId, setResubmitFromApprovalId] = useState(null);
  useEffect(() => {
    const onStorage = () => setApprovals(readApprovals());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  // Refresh on tab change
  useEffect(() => { setApprovals(readApprovals()); }, [tab]);
  // Clear course selection when leaving the courses tab
  useEffect(() => { if (tab !== 'courses') setSelectedCourseId(null); }, [tab]);
  // Clear resubmit context when leaving the submit tab
  useEffect(() => { if (tab !== 'submit') setResubmitFromApprovalId(null); }, [tab]);
  return {
    tab, setTab, approvals, setApprovals,
    selectedCourseId, setSelectedCourseId,
    resubmitFromApprovalId, setResubmitFromApprovalId,
  };
};

const TP_Dashboard = ({ approvals, setTab, setSelectedCourseId, setResubmitFromApprovalId }) => {
  const pending = approvals.filter((a) => a.provider === 'Collin College' && a.status === 'pending').length;
  const [notifs, unread] = useNotifications('training_provider');
  const [openNotif, setOpenNotif] = useState(null);
  const openDetail = (n) => {
    setOpenNotif(n);
    if (!n.read) markRead(n.id);
  };
  return (
    <>
      <ScreenHeader title="Provider Dashboard" subtitle={`${IDENTITIES.training_provider.name} · ${IDENTITIES.training_provider.facility}`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section className="grid grid-cols-2 gap-2" aria-label="Provider stats">
          <StatCard label="Published Courses" value={TP_COURSES.length} />
          <StatCard label="Total Enrollments" value={247} />
          <StatCard label="Avg Completion" value="74%" />
          <StatCard label="Pell Revenue YTD" value="$18,400" accent />
        </section>
        {notifs.length > 0 && (
          <section aria-label="Inbox" data-testid="tp-inbox">
            <SectionTitle right={
              <span className="inline-flex items-center gap-2">
                {unread > 0 ? <span className="p-mono font-bold" style={{ color: '#c2410c' }}>{unread} new</span> : <span style={{ color: '#94a3b8' }}>all read</span>}
                {unread > 0 && (
                  <button onClick={() => markAllRead('training_provider')} data-testid="tp-mark-all-read" className="text-[12px] font-semibold text-[#f97316] hover:underline">Mark all read</button>
                )}
              </span>
            }>Inbox</SectionTitle>
            <ul className="list-none p-0 space-y-2">
              {notifs.slice(0, 3).map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => openDetail(n)}
                    className="w-full text-left rounded-[12px] p-3 flex items-start gap-2 transition-colors hover:brightness-105"
                    style={{ background: n.read ? '#f8fafc' : 'rgba(249,115,22,0.08)', border: n.read ? '1px solid #e2e8f0' : '1px solid rgba(249,115,22,0.35)' }}
                    data-testid={`tp-notif-${n.type}`}
                    aria-label={`Open notification: ${n.title}`}
                  >
                    <div className="mt-0.5 text-[16px]" aria-hidden="true">{n.type === 'course_approved' ? '✅' : '📝'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#0f2744]">{n.title}</div>
                      <div className="text-[12px] text-[#64748b] mt-0.5 truncate">{n.body}</div>
                      <div className="text-[10px] p-mono text-[#94a3b8] mt-1">{new Date(n.at).toLocaleString()}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: '#f97316' }} aria-hidden="true" />}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {pending > 0 && (
          <div className="rounded-[12px] p-4 flex items-center justify-between" style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid #f97316' }}>
            <div>
              <div className="text-[13px] font-semibold" style={{ color: '#c2410c' }}><Mono>{pending}</Mono> course{pending === 1 ? '' : 's'} pending facility approval</div>
              <div className="text-[12px] text-[#78350f] mt-0.5">Awaiting review from Lt. Carlos Mendoza at Collin County DC</div>
            </div>
            <button onClick={() => setTab('courses')} data-testid="tp-view-pending" className="text-[13px] font-semibold" style={{ color: '#c2410c' }}>Status →</button>
          </div>
        )}
        <section aria-label="Published courses">
          <SectionTitle right="3 active">Published Courses</SectionTitle>
          <ul className="list-none p-0 space-y-2">
            {TP_COURSES.map((c) => (
              <li key={c.name} className="rounded-[12px] bg-[#f1f5f9] p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold text-[#0f2744]">{c.name}</div>
                    <div className="text-[12px] text-[#64748b] mt-0.5"><Mono>{c.enrolled}</Mono> enrolled · <Mono>{c.completions}</Mono> completed · <Mono>{c.completionPct}</Mono>%</div>
                  </div>
                  <Badge tone="success">Active</Badge>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </main>
      {openNotif && (
        <div className="absolute inset-0 z-[150] flex items-end" role="dialog" aria-modal="true" data-testid="tp-notif-drawer">
          <div className="absolute inset-0 bg-black/40" data-testid="tp-notif-backdrop" onClick={() => setOpenNotif(null)} />
          <div
            className="relative w-full rounded-t-[16px] bg-white p-5"
            style={{ maxHeight: '75%', overflowY: 'auto', animation: 'proto-fade-up 220ms ease-out both' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="text-[28px]" aria-hidden="true">{openNotif.type === 'course_approved' ? '✅' : '📝'}</div>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#f97316]">{openNotif.type === 'course_approved' ? 'Course Approved' : 'Revision Requested'}</div>
                  <h2 className="p-display text-[18px] mt-0.5 leading-tight" style={{ color: '#0f2744', margin: 0 }}>{openNotif.title}</h2>
                </div>
              </div>
              <button onClick={() => setOpenNotif(null)} data-testid="tp-notif-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9]" aria-label="Close">✕</button>
            </div>
            <p className="text-[14px] text-[#334155] mt-3 leading-relaxed">{openNotif.body}</p>
            <div className="mt-4 rounded-[10px] bg-[#f8fafc] p-3 space-y-1">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#64748b]">From</div>
              <div className="text-[13px] text-[#0f2744]">Lt. Carlos Mendoza · Facility Admin, Collin County DC</div>
              <div className="text-[11px] p-mono text-[#94a3b8] mt-1">{new Date(openNotif.at).toLocaleString()}</div>
            </div>
            <div className="mt-4 flex gap-2">
              {openNotif.type === 'revision_requested' ? (
                <>
                  <button
                    onClick={() => {
                      const approvalId = openNotif?.meta?.approvalId;
                      if (approvalId) setResubmitFromApprovalId(approvalId);
                      setOpenNotif(null);
                      setTab('submit');
                    }}
                    data-testid="tp-notif-fix-resubmit"
                    className="flex-1 min-h-[44px] rounded-[10px] px-4 font-bold text-[14px]"
                    style={{ background: '#f97316', color: '#0f2744' }}
                  >Fix &amp; resubmit →</button>
                  <button
                    onClick={() => {
                      const approvalId = openNotif?.meta?.approvalId;
                      if (approvalId) setSelectedCourseId(approvalId);
                      setOpenNotif(null);
                      setTab('courses');
                    }}
                    data-testid="tp-notif-view-course"
                    className="min-h-[44px] rounded-[10px] px-4 font-semibold text-[14px]"
                    style={{ background: 'transparent', color: '#c2410c', border: '1px solid #f97316' }}
                  >View details</button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      const approvalId = openNotif?.meta?.approvalId;
                      if (approvalId) setSelectedCourseId(approvalId);
                      setOpenNotif(null);
                      setTab('courses');
                    }}
                    data-testid="tp-notif-view-course"
                    className="flex-1 min-h-[44px] rounded-[10px] px-4 font-bold text-[14px]"
                    style={{ background: '#f97316', color: '#0f2744' }}
                  >View course →</button>
                  <button
                    onClick={() => setOpenNotif(null)}
                    data-testid="tp-notif-close-button"
                    className="min-h-[44px] rounded-[10px] px-4 font-semibold text-[14px]"
                    style={{ background: 'transparent', color: '#64748b', border: '1px solid #e2e8f0' }}
                  >Close</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const TP_CourseDetail = ({ approval, onBack }) => {
  const statusMeta = approval.status === 'approved'
    ? { label: 'Approved', tone: 'success', accent: '#16a34a', bg: 'rgba(22,163,74,0.10)', border: 'rgba(22,163,74,0.35)', icon: '✅', headline: 'This course is live at Collin County DC.' }
    : approval.status === 'revision'
    ? { label: 'Revision Requested', tone: 'orange', accent: '#c2410c', bg: 'rgba(249,115,22,0.10)', border: 'rgba(249,115,22,0.35)', icon: '📝', headline: 'The Facility Admin requested changes. Update and re-submit.' }
    : { label: 'Pending Approval', tone: 'orange', accent: '#c2410c', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.35)', icon: '⏳', headline: 'Awaiting review from Lt. Carlos Mendoza.' };
  return (
    <>
      <ScreenHeader
        title={approval.title}
        subtitle={`Submitted by ${approval.provider} · ${approval.submitted}`}
        onBack={onBack}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4" data-testid="tp-course-detail">
        <div className="rounded-[12px] p-4 flex items-start gap-3" style={{ background: statusMeta.bg, border: `1px solid ${statusMeta.border}` }}>
          <div className="text-[24px]" aria-hidden="true">{statusMeta.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone={statusMeta.tone}>{statusMeta.label}</Badge>
              <Badge tone={approval.kind === 'new' ? 'orange' : 'navy'}>{approval.kind === 'new' ? 'New Module' : 'Update'}</Badge>
            </div>
            <div className="text-[13px] mt-2" style={{ color: statusMeta.accent }}>{statusMeta.headline}</div>
          </div>
        </div>
        {approval.summary && (
          <section aria-label="Summary">
            <SectionTitle>Summary</SectionTitle>
            <p className="text-[14px] text-[#1c1c1e] leading-relaxed">{approval.summary}</p>
          </section>
        )}
        {approval.kind === 'new' && (
          <section aria-label="Course specs">
            <SectionTitle>Specs</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {approval.credential && <Chip>{approval.credential}</Chip>}
              {approval.hours && <Chip><Mono>{approval.hours}</Mono> clock hours</Chip>}
            </div>
          </section>
        )}
        <section aria-label="Review trail">
          <SectionTitle>Review Trail</SectionTitle>
          <ul className="list-none p-0 space-y-2">
            <li className="rounded-[10px] bg-[#f8fafc] p-3">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#64748b]">Submitted</div>
              <div className="text-[13px] text-[#0f2744] mt-0.5">{approval.provider} · {approval.submitted}</div>
            </li>
            {approval.status !== 'pending' && (
              <li className="rounded-[10px] p-3" style={{ background: statusMeta.bg, border: `1px solid ${statusMeta.border}` }}>
                <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: statusMeta.accent }}>{approval.status === 'approved' ? 'Approved by' : 'Revision requested by'}</div>
                <div className="text-[13px] text-[#0f2744] mt-0.5">Lt. Carlos Mendoza · Facility Admin, Collin County DC</div>
              </li>
            )}
          </ul>
        </section>
      </main>
    </>
  );
};

const TP_Courses = ({ approvals, setTab, selectedCourseId, setSelectedCourseId }) => {
  const pending = approvals.filter((a) => a.provider === 'Collin College');
  const selected = selectedCourseId ? approvals.find((a) => a.id === selectedCourseId) : null;
  if (selected) {
    return <TP_CourseDetail approval={selected} onBack={() => setSelectedCourseId(null)} />;
  }
  return (
    <>
      <ScreenHeader title="Published Courses" subtitle={`${TP_COURSES.length} active · ${pending.filter((p) => p.status === 'pending').length} pending`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        {pending.map((p) => (
          <button
            type="button"
            key={p.id}
            onClick={() => setSelectedCourseId(p.id)}
            data-testid={`tp-course-item-${p.id}`}
            className="w-full text-left rounded-[12px] p-4 transition-colors hover:brightness-105"
            style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.35)' }}
          >
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-[#0f2744]">{p.title}</div>
                <div className="text-[12px] text-[#64748b] mt-0.5">Submitted {p.submitted}</div>
              </div>
              <Badge tone={p.status === 'approved' ? 'success' : p.status === 'revision' ? 'orange' : 'orange'}>
                {p.status === 'approved' ? 'Approved' : p.status === 'revision' ? 'Revision Requested' : 'Pending Approval'}
              </Badge>
            </div>
            <div className="text-[12px] mt-2 font-semibold" style={{ color: '#c2410c' }}>View detail →</div>
          </button>
        ))}
        {TP_COURSES.map((c) => (
          <div key={c.name} className="rounded-[12px] bg-[#f1f5f9] p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-[#0f2744]">{c.name}</div>
                <div className="text-[12px] text-[#64748b] mt-0.5">{c.credential} · <Mono>{c.hours}</Mono> hrs · <Mono>{c.enrolled}</Mono> enrolled</div>
              </div>
              <Badge tone="success">Active</Badge>
            </div>
          </div>
        ))}
      </main>
    </>
  );
};

const TPField = ({ label, children }) => (
  <div>
    <label className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const TP_Submit = ({ approvals, setApprovals, setTab, resubmitFromApprovalId, setResubmitFromApprovalId }) => {
  const { showModal, showToast } = useUI();
  const resubmitFor = resubmitFromApprovalId ? approvals.find((a) => a.id === resubmitFromApprovalId) : null;
  // Strip "New Module: " prefix so we don't nest it when resubmitting
  const cleanTitle = (t) => (t || '').replace(/^New Module:\s*/, '');
  const defaultTitle = resubmitFor ? cleanTitle(resubmitFor.title) : 'Electrical Fundamentals — NCCER Level 1';
  const [title, setTitle] = useState(defaultTitle);
  const [hours, setHours] = useState(resubmitFor?.hours || 45);
  const [credential] = useState(resubmitFor?.credential || 'NCCER Level 1 Certification');

  // If the resubmit context changes (drawer → submit navigation), refresh the fields.
  useEffect(() => {
    if (resubmitFor) {
      setTitle(cleanTitle(resubmitFor.title));
      setHours(resubmitFor.hours || 45);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resubmitFromApprovalId]);

  const isResubmit = !!resubmitFor;
  const submit = () => showModal({
    title: isResubmit ? `Resubmit ${title} for review?` : `Submit ${title} for facility review?`,
    body: isResubmit
      ? 'Lt. Mendoza will be notified this course has been revised. Approval typically resumes within 2–3 business days.'
      : 'Lt. Mendoza at Collin County DC will be notified. Review typically takes 5–7 business days.',
    confirmLabel: isResubmit ? 'Resubmit' : 'Submit',
    onConfirm: () => {
      if (isResubmit) {
        // Flip the existing approval back to pending. Preserve the semantic
        // title shape: 'New Module: X' only if the original kind was 'new';
        // otherwise use the raw (update-style) title verbatim.
        const cleaned = cleanTitle(title);
        const nextTitle = resubmitFor.kind === 'new' ? `New Module: ${cleaned}` : cleaned;
        const next = approvals.map((a) => a.id === resubmitFromApprovalId
          ? { ...a, status: 'pending', submitted: 'Just now (revision)', title: nextTitle, hours }
          : a
        );
        writeApprovals(next);
        setApprovals(next);
        // Cross-role notification back to Facility Admin
        postNotification({
          from: 'training_provider',
          to: 'facility_admin',
          type: 'course_resubmitted',
          title: `Resubmitted: ${cleaned}`,
          body: 'Collin College made the requested revisions. Ready for re-review.',
          meta: { approvalId: resubmitFromApprovalId },
        });
        showToast('Course resubmitted — Lt. Mendoza has been notified.', 'success');
        setResubmitFromApprovalId(null);
      } else {
        const next = [...approvals, {
          id: `custom-${Date.now()}`,
          title: `New Module: ${title}`,
          provider: 'Collin College',
          submitted: 'Just now',
          kind: 'new',
          credential,
          hours,
          status: 'pending',
        }];
        writeApprovals(next);
        setApprovals(next);
        showToast('Course submitted — review within 5–7 business days.', 'success');
      }
      setTab('courses');
    },
  });
  return (
    <>
      <ScreenHeader
        title={isResubmit ? 'Resubmit After Revision' : 'Submit New Course'}
        subtitle={isResubmit ? `Revising ${cleanTitle(resubmitFor.title)}` : 'For marketplace review'}
        onBack={isResubmit ? () => { setResubmitFromApprovalId(null); setTab('dashboard'); } : undefined}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        {isResubmit && (
          <div
            className="rounded-[12px] p-4"
            style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid #f97316' }}
            data-testid="tp-resubmit-banner"
          >
            <div className="flex items-start gap-2">
              <div className="text-[22px]" aria-hidden="true">📝</div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#c2410c' }}>Revision Requested</div>
                <div className="text-[13px] font-semibold text-[#0f2744] mt-0.5">Lt. Carlos Mendoza asked for changes.</div>
                <div className="text-[12px] text-[#78350f] mt-1">Fields below are pre-filled from your original submission. Adjust as needed, then hit <span className="font-bold">Resubmit</span>. The approval will flip back to Pending and Lt. Mendoza will get an inbox notification.</div>
              </div>
            </div>
          </div>
        )}
        <TPField label="Course title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="tp-input-title" className="w-full min-h-[48px] rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 text-[14px] focus:outline-none focus:border-[#f97316]" />
        </TPField>
        <div className="grid grid-cols-2 gap-3">
          <TPField label="Clock hours">
            <input type="number" value={hours} onChange={(e) => setHours(Number(e.target.value) || 0)} data-testid="tp-input-hours" className="w-full min-h-[48px] rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 text-[14px] focus:outline-none focus:border-[#f97316]" style={{ fontFamily: 'var(--p-font-mono)' }} />
          </TPField>
          <TPField label="Credential body">
            <div className="w-full min-h-[48px] rounded-[12px] bg-[#f1f5f9] px-4 flex items-center text-[13px] text-[#0f2744]">NCCER</div>
          </TPField>
        </div>
        <TPField label="Credential pathway">
          <div className="w-full min-h-[48px] rounded-[12px] bg-[#f1f5f9] px-4 flex items-center text-[14px] text-[#0f2744]">{credential}</div>
        </TPField>
        <TPField label="Description">
          <textarea rows={3} defaultValue={resubmitFor?.summary || "Level 1 introduction to residential and light commercial electrical systems. Covers NEC basics, safe wiring practices, and hand tool proficiency."} className="w-full rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 py-3 text-[14px] focus:outline-none focus:border-[#f97316]" />
        </TPField>
        <div className="flex items-center justify-between rounded-[12px] bg-[#f1f5f9] p-3">
          <div><div className="text-[14px] font-semibold text-[#0f2744]">Workforce Pell eligibility</div><div className="text-[12px] text-[#64748b] mt-0.5">SOC 47-2111 mapped</div></div>
          <span className="rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: '#15803d', color: '#fafaf9' }}>ON</span>
        </div>
        <Btn variant="ghost" onClick={() => showToast('File attached: NCCER_Electrical_L1.pdf', 'success')} className="w-full" data-testid="tp-upload"><Plus className="w-4 h-4" /> Upload Curriculum Outline</Btn>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn onClick={submit} className="w-full" data-testid={isResubmit ? 'tp-resubmit' : 'tp-submit'}>{isResubmit ? 'Resubmit for Review' : 'Submit for Review'}</Btn>
      </footer>
    </>
  );
};

const TP_Analytics = ({ setTab }) => (
  <>
    <ScreenHeader title="Learner Analytics" subtitle="HVAC Fundamentals · anonymized" />
    <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
      <section className="grid grid-cols-2 gap-2">
        <StatCard label="Enrolled" value={94} />
        <StatCard label="Completed" value={68} />
        <StatCard label="Avg Score" value="74%" />
        <StatCard label="Avg Days" value="8.2" />
      </section>
      <section>
        <SectionTitle>Lesson Completion Funnel</SectionTitle>
        <div className="rounded-[12px] bg-[#f1f5f9] p-4 space-y-2">
          {[
            ['L1', 94], ['L2', 91], ['L3', 88], ['L4', 82], ['L5', 79], ['L6', 74], ['L7', 70], ['L8', 68],
          ].map(([l, v]) => (
            <div key={l} className="flex items-center gap-2">
              <div className="w-8 text-[12px] font-semibold text-[#64748b]">{l}</div>
              <div className="flex-1 h-4 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
                <div className="h-full" style={{ width: `${(v / 94) * 100}%`, background: '#f97316' }} />
              </div>
              <div className="w-8 text-[12px] text-right text-[#0f2744]" style={{ fontFamily: 'var(--p-font-mono)' }}>{v}</div>
            </div>
          ))}
        </div>
      </section>
      <p className="text-[13px] italic text-[#64748b]">
        Most learners who exit do so between Lessons 4–5. Consider reviewing content at this checkpoint.
      </p>
    </main>
  </>
);

export const TrainingProvider = () => {
  const s = useTPState();
  const [, unreadTP] = useNotifications('training_provider');
  let screen;
  if (s.tab === 'courses') screen = <TP_Courses {...s} />;
  else if (s.tab === 'submit') screen = <TP_Submit {...s} />;
  else if (s.tab === 'analytics') screen = <TP_Analytics setTab={s.setTab} />;
  else screen = <TP_Dashboard {...s} />;
  return (
    <StaffShell
      items={TP_NAV}
      activeKey={s.tab}
      onChange={s.setTab}
      badge={{ initial: 'S', name: IDENTITIES.training_provider.name.replace('Dr. ', ''), role: 'Training Provider' }}
      itemBadges={{ dashboard: unreadTP }}
      footerUnread={unreadTP}
    >
      {screen}
    </StaffShell>
  );
};
